import { getQueryParamByName } from '@dg-util/getQueryParam'
import { CONTEXT_CACHE_KEY } from '@dg-shared/ContextProvider'
import { GDPRDataType } from '@dg-shared/AcceptCookies'
import { PolicyV2Type, ProductPropV2Type, ResponseErrorV2Type, VehicleV2Type } from '../types/ApiV2'
import { format, endOfDay } from 'date-fns'
import {
  ERROR_TYPE,
  ErrorCode,
  ErrorStatus,
  FORMAT_DAY_MONTH_YEAR_DOT,
  localizedPartnerName,
} from '../constants'
import { Events, PolicyStatus } from '../FSM/shared/constants'
import { FlowServiceType } from '../types/interface/fsm.interface'
import { PartnerName, ProductName } from '../types/interface/quote.interface'
import { getProductName } from '@dg-util/productHandler'

export { default as getCookieByName } from './getCookieByName'
export { getQueryParam, getQueryParamByName } from './getQueryParam'
export { default as normalizeFormat } from './normalizeFormat'
export { default as setPageError } from './setPageError'
export { default as urlIdentifier } from './urlIdentifier'
export { default as defineOfferDate } from './defineOfferDate'
export { default as formatPolicyDate } from './formatPolicyDate'
export { default as getCoverageData } from './getCoverageData'
export { default as setCookie } from './setCookie'
export { default as normalizeMotorRegistrationNumber } from './normalizeMotorRegistrationNumber'
export { default as getBackButtonText } from './getBackButtonText'
export { getProductName, getFlowName } from './productHandler'
export {
  setSessionStorageAuthItem,
  setSessionAuthMethod,
  getSessionAuthMethod,
} from './sessionStorage'
export { default as redirect } from './redirectExternal'
export { safeSessionStorageClear, fullStorageCleanup, useFullStorageCleanup } from './storageClear'
export { default as addSpaceRegNum } from './addSpaceRegNum'
export { default as updateDateWithTimeISO } from './updateDateWithTimeISO'
export { base64ToArrayBuffer, saveByteArray } from './fileDownload'
export { formatNorwegianAccountNumber, normalizePaymentMethod } from './formatInvoice'

export const getSearchParams = () =>
  new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop as string),
  }) as unknown as Record<string, string>

export const DATA_LAYER_UTM_CACHE_KEY = 'DATA_LAYER_UTM_CACHE_KEY'

export const getGDPRSettings = (): GDPRDataType => {
  const contextCache = JSON.parse(localStorage.getItem(CONTEXT_CACHE_KEY))
  return contextCache?.acceptCookies || {}
}

export type DataLayerSettingsType = {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_id: string
  utm_term: string
  utm_content: string
  gdpr_setting: GDPRDataType
}

export const getDataLayerSettings = (): DataLayerSettingsType => {
  // Check if UTM is passed through the url
  let dataLayerUTMData = {
    utm_source: getQueryParamByName('utm_source'),
    utm_medium: getQueryParamByName('utm_medium'),
    utm_campaign: getQueryParamByName('utm_campaign'),
    utm_id: getQueryParamByName('utm_id'),
    utm_term: getQueryParamByName('utm_term'),
    utm_content: getQueryParamByName('utm_content'),
  }

  // Preserve DATA_LAYER_UTM for future usage if present
  if (dataLayerUTMData.utm_source || dataLayerUTMData.utm_id) {
    localStorage.setItem(DATA_LAYER_UTM_CACHE_KEY, JSON.stringify(dataLayerUTMData))
  } else {
    // Get Preserved DATA_LAYER_UTM if available
    dataLayerUTMData = JSON.parse(localStorage.getItem(DATA_LAYER_UTM_CACHE_KEY)) || {}
  }

  const gdpr_setting = getGDPRSettings()

  return {
    ...dataLayerUTMData,
    gdpr_setting,
  }
}

const SELLING_CHANNEL_QUERY_PARAM = 'source'
export const SELLING_CHANNEL_CACHE_KEY = 'SELLING_CHANNEL_CACHE_KEY'

export const getSellingChannel = () => {
  let sellingChannel = getQueryParamByName(SELLING_CHANNEL_QUERY_PARAM)

  if (sellingChannel) {
    // Set selling channel in cache
    localStorage.setItem(SELLING_CHANNEL_CACHE_KEY, sellingChannel)
  } else {
    // Get Preserved sellingChannel if available
    sellingChannel = localStorage.getItem(SELLING_CHANNEL_CACHE_KEY)
  }

  return sellingChannel
}

export const clearSellingChannel = () => localStorage.removeItem(SELLING_CHANNEL_CACHE_KEY)

export type QueryParamsDataType = {
  dataLayerSettings: DataLayerSettingsType
  sellingChannel: string
  productName: ProductName
}

export const getQueryParamsData = (): QueryParamsDataType => {
  return {
    dataLayerSettings: getDataLayerSettings(),
    sellingChannel: getSellingChannel(),
    productName: getProductName(),
  }
}

export const getDataComponentName = (filename: string) =>
  filename
    .substr(filename.indexOf('src') + 4, filename.length)
    .replace('.tsx', '')
    .replace('.', '/')

export const getPolicyDates = (policy: PolicyV2Type) => {
  return {
    dateFrom:
      !!policy.InsuredFrom && format(new Date(policy.InsuredFrom), FORMAT_DAY_MONTH_YEAR_DOT),
    dateTo: !!policy.InsuredTo && format(new Date(policy.InsuredTo), FORMAT_DAY_MONTH_YEAR_DOT),
  }
}

/**
 * The instant a policy's cover actually lapses.
 *
 * `InsuredTo` is the INCLUSIVE last day of cover, delivered as a bare midnight
 * timestamp — "2026-09-07T00:00:00" means "covered through the 7th", not "expires as
 * the 7th begins". Comparing it directly against `now` therefore reports a policy as
 * expired for the whole of its final day.
 *
 * Harmless until an endorsement: i2i closes the running version *today* and opens the
 * new one tomorrow, and a version-less fetch returns the still-current version. So
 * right after a change the policy's `InsuredTo` is midnight today and it vanished from
 * My Pages until midnight, then came back on its own (DFE-4112).
 */
const getPolicyCoverageEnd = (policy: Partial<PolicyV2Type>): Date =>
  endOfDay(new Date(policy.InsuredTo))

/**
 * True while `at` falls on or before the policy's last day of cover.
 *
 * A policy with no `InsuredTo` yields an invalid date and therefore `false`, which is
 * what the direct comparisons this replaces already did.
 */
export const hasPolicyCoverageRemaining = (
  policy: Partial<PolicyV2Type>,
  at: Date = new Date()
): boolean => getPolicyCoverageEnd(policy).getTime() >= at.getTime()

/**
 * When a change already registered on this policy takes effect, or `null` if there is none.
 *
 * A completed change closes the running version and opens the next one from the change
 * date, so a version starting after today IS a scheduled change — there is no other way
 * for one to exist. Read off `Versions`, which every policy payload already carries, so
 * this costs no request.
 *
 * Used to explain why the policy cannot be changed again right now: i2i refuses a change
 * dated outside the loaded version's period, and the version-less fetch returns the
 * still-active one (DFE-4112). When that version was clipped to end today, every date the
 * change form offers (tomorrow onwards) is already past it.
 */
export const getScheduledChangeStart = (policy: Partial<PolicyV2Type>): Date | null => {
  const endOfToday = endOfDay(new Date()).getTime()

  const upcoming = (policy.Versions ?? [])
    .map((version) => new Date(version.InsuredFrom))
    .filter((startsAt) => startsAt.getTime() > endOfToday)
    .sort((a, b) => a.getTime() - b.getTime())

  return upcoming[0] ?? null
}

export const getProductPropValue = (
  productProp: ProductPropV2Type,
  productPropValueKey: string | number
) => {
  const productPropItem = productProp.values.find((item) => item[productPropValueKey])
  return productPropItem?.[productPropValueKey]
}

export const filterVehiclesWithoutPolicies = (
  vehicles: VehicleV2Type[],
  policies: Partial<PolicyV2Type>[]
) => {
  const policiesMotorRegistrationNumbers = policies.map((policy) => policy.MotorRegistrationNumber)
  return vehicles.filter(
    (vehicle) => !policiesMotorRegistrationNumbers.includes(vehicle.MotorRegistrationNumber)
  )
}

export const reactQueryErrorHandler = (
  error: ResponseErrorV2Type,
  flowService: FlowServiceType,
  backState?: string
) => {
  const eventTypeByErrorStatusMap: Record<ErrorStatus, Events> = {
    [ErrorStatus.UNAUTHORIZED]: Events.AUTH_ERROR,
    [ErrorStatus.SESSION_EXPIRED]: Events.SESSION_EXPIRED,
    [ErrorStatus.PROXY_AUTH_REQUIRED]: Events.AUTH_ERROR,
    [ErrorStatus.LOGIN_TIMEOUT]: Events.SESSION_EXPIRED,
  }

  const eventTypeByErrorCodeMap: Record<ErrorCode, Events> = {
    [ErrorCode.ERROR_CODE_QUOTE_VALIDATION]: Events.ERROR,
    [ErrorCode.ERROR_CODE_QUOTE_CONSTRAINTS]: Events.ERROR,
    [ErrorCode.ERROR_CODE_QUOTE_GENERAL]: Events.ERROR,
    [ErrorCode.ERROR_CODE_UNKNOWN_CHARACTERISTICS]: Events.WRONG_PRODUCT_ID,
    [ErrorCode.ERROR_CODE_AUTH_ACCOUNT_LOCKED]: Events.AUTH_ERROR,
    [ErrorCode.ERROR_CODE_NO_QUOTE]: Events.ERROR,
    [ErrorCode.ERROR_CODE_REG_NUM]: Events.WRONG_PRODUCT_ID,
    [ErrorCode.ERROR_CODE_SESSION_EXPIRED]: Events.SESSION_EXPIRED,
    [ErrorCode.ERROR_CODE_BANKID_OUT]: Events.ERROR,
  }

  const errorCode: ErrorCode = error.response?.error?.code

  const eventTypeByErrorCode = eventTypeByErrorCodeMap[errorCode]
  const eventTypeByErrorStatus = eventTypeByErrorStatusMap[error.status] || Events.ERROR

  // Set Event Type based on Error Code or Error Status (according priorities)
  const errorEvent = eventTypeByErrorCode || eventTypeByErrorStatus || Events.ERROR

  flowService.send({
    type: errorEvent,
    errorType: ERROR_TYPE[errorCode],
    state: flowService.getSnapshot().value,
    backState, // Event property helps to identify where to navigate back on an error page
  })
}

export const isCurrentActivePolicy = (policy: Partial<PolicyV2Type>) => {
  const now = new Date()
  const policyInsuredFromTimestamp = new Date(policy.InsuredFrom).getTime()

  return (
    policyInsuredFromTimestamp < now.getTime() &&
    // Not `InsuredTo > now`: that reads the inclusive last day of cover as an expiry
    // instant and makes the policy uneditable for the whole of that day (DFE-4112).
    hasPolicyCoverageRemaining(policy, now) &&
    [PolicyStatus.ACCEPTED, PolicyStatus.VALID].includes(policy.PolicyStatus)
  )
}

// Check if the error is critical (requires sign-out and redirect to auth page)
export const isCriticalError = (code: ErrorCode): boolean =>
  [
    ErrorCode.ERROR_CODE_QUOTE_CONSTRAINTS,
    ErrorCode.ERROR_CODE_QUOTE_VALIDATION,
    ErrorCode.ERROR_CODE_NO_QUOTE,
  ].includes(code)

export const getLocalizedPartnerName = () => localizedPartnerName[PARTNER_NAME as PartnerName]
